import { useState } from 'react';
import './ServicesCard.css';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Button } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import FingerprintOutlinedIcon from '@mui/icons-material/FingerprintOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import BiotechOutlinedIcon from '@mui/icons-material/BiotechOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';

/* Feature icons are named by string in database/services.jsx so that data file
   stays free of component imports — ServicePage loads it too. Same icon set the
   kits page draws from, so the two pages read as one system. */
const ICONS = {
  doneAll: DoneAllIcon,
  verified: VerifiedOutlinedIcon,
  tree: AccountTreeOutlinedIcon,
  assessment: AssessmentOutlinedIcon,
  fingerprint: FingerprintOutlinedIcon,
  hub: HubOutlinedIcon,
  science: ScienceOutlinedIcon,
  storage: StorageOutlinedIcon,
  search: ManageSearchIcon,
  biotech: BiotechOutlinedIcon,
  group: GroupOutlinedIcon,
  trendingDown: TrendingDownIcon,
  speed: SpeedOutlinedIcon,
  tune: TuneOutlinedIcon,
};

const highlightScisGo = (text) => {
  if (!text?.includes('ScisGo')) return text;
  const parts = text.split('ScisGo');
  return parts.reduce((acc, part, i) => {
    if (i === parts.length - 1) return [...acc, part];
    return [...acc, part, 'Scis', <span key={i} style={{ color: 'var(--orange)' }}>Go</span>];
  }, []);
};

/* Chimerism and Targeted Variation have no documents yet, recorded as '#' or ''
   in the data. The old card rendered their links anyway, so they pointed
   nowhere — only render a link when there is a real file behind it. */
const hasFile = (value) => Boolean(value) && value !== '#';

const ServicesCard = ({ services, theme }) => {
  const [zoomedImg, setZoomedImg] = useState(null);

  return (
    <>
      {services.map((s, i) => (
        /* id={s.nickname} is load-bearing: the navbar Services dropdown deep
           links to /services#hla, #kir, #micab, #fcr, #chimerism and
           #targeted-variation. */
        <section
          key={s.key}
          id={s.nickname}
          className={`service-section${i % 2 ? '' : ' service-section-alt'}`}
        >
          <div className='service-section-inner'>
            <div className='service-badge-row'>
              <img src={s.mini_img} className='service-icon' alt='' />
              <span className='service-badge'>{s.services_page_title}</span>
            </div>
            <div className='service-content'>
              <div className='service-text'>
                <h2 className='service-main-headline'>
                  {highlightScisGo(s.headline)}
                </h2>
                <p className='service-desc'>{s.services_page_description}</p>
                {s.spec && (
                  <div className='service-spec'>
                    <span className='service-spec-title'>{s.spec.title}</span>
                    <div className='service-spec-grid'>
                      {s.spec.items.map((item) => (
                        <span key={item} className='service-spec-item'>
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <img
                src={s.services_page_image}
                alt={s.services_page_title}
                className='service-img service-img-clickable'
                onClick={() => setZoomedImg(s.services_page_image)}
              />
            </div>
            {s.features && (
              <div className='service-features'>
                {s.features.map((feature) => {
                  const Icon = ICONS[feature.icon];
                  return (
                    <div key={feature.text} className='service-feature'>
                      {Icon && <Icon className='service-feature-icon' />}
                      <span>{feature.text}</span>
                    </div>
                  );
                })}
              </div>
            )}
            <div className='service-actions'>
              {hasFile(s.sample_report) && (
                <a
                  className='service-dl-link'
                  href={s.sample_report}
                  target='_blank'
                  rel='noreferrer'
                >
                  Sample {s.nickname} report <FileDownloadOutlinedIcon />
                </a>
              )}
              {hasFile(s.service_pdf) && (
                <a
                  className='service-dl-link'
                  href={s.service_pdf}
                  target='_blank'
                  rel='noreferrer'
                >
                  {s.nickname} service details <FileDownloadOutlinedIcon />
                </a>
              )}
              <ThemeProvider theme={theme}>
                <Button
                  href={s.link}
                  color='primary'
                  variant='contained'
                  sx={{ borderRadius: '30px', fontWeight: 700, fontSize: '17.5px' }}
                >
                  Learn more
                </Button>
              </ThemeProvider>
            </div>
          </div>
        </section>
      ))}

      {zoomedImg && (
        <div
          className='service-lightbox-overlay'
          onClick={() => setZoomedImg(null)}
        >
          <img src={zoomedImg} className='service-lightbox-img' alt='zoomed' />
        </div>
      )}
    </>
  );
};

export default ServicesCard;
